<template>
  <div>
    <div class="mode-toggle panzoom-ignore">
      <v-btn-toggle v-model="liveChatData.editorMode" mandatory dense>
        <v-btn value="tree" small>Tree</v-btn>
        <v-btn value="json" small>JSON</v-btn>
      </v-btn-toggle>
    </div>

    <LiveChatJsonMode v-if="liveChatData.editorMode === 'json'" :live-chat-data="liveChatData" />

    <template v-else>
      <div v-if="flowError" class="flow-error panzoom-ignore">{{ flowError }}</div>
      <div class="zoom-actions">
        <v-btn @click="zoomIn">+</v-btn>
        <v-btn class="ml-2" @click="zoomOut">-</v-btn>
        <v-btn class="ml-2" @click="reset">
          <v-icon class="mr-1" color="icon" small>mdi-restart</v-icon>
        </v-btn>
      </div>

      <div class="panzoom-parent">
        <div ref="panzoom">
          <!--Chatbot settings -->
          <div class="d-flex justify-center">
            <div class="chatbot-data">
              <div class="title mb-2">
                {{ $t("shortcodes.live_chat.chatbot_settings") }}
              </div>
              <v-text-field
                v-model="liveChatData.botName"
                persistent-placeholder
                :label="'Chatbot name'"
                :placeholder="'Mary'"></v-text-field>
              <VaMediaObjectFileInput
                label="Chatbot avatar"
                class="mb-4"
                :item-id="liveChatData.botAvatar.imageID"
                @update:photo="updateAvatar($event)" />

              <va-select-input
                class="mb-4"
                v-model="liveChatData.theme"
                :choices="LIVE_CHAT_THEME"
                :label="'Theme'"
                clearable></va-select-input>

              <VaColorPicker
                class="mb-4 panzoom-ignore"
                :title="'LiveChat (bot) message background color'"
                v-model="liveChatData.botMessageBackgroundColor"></VaColorPicker>

              <VaColorPicker
                class="mb-4 panzoom-ignore"
                :title="'Action items background color'"
                v-model="liveChatData.actionItemBackgroundColor"></VaColorPicker>

              <VaColorPicker
                class="mb-4 panzoom-ignore"
                :title="'User message background color'"
                v-model="liveChatData.userMessageBackgroundColor"></VaColorPicker>
              <VaColorPicker
                class="mb-4 panzoom-ignore"
                :title="'User message text color'"
                v-model="liveChatData.userMessageTextColor"></VaColorPicker>
              <div class="counter-bottom-settings">
                <div class="d-flex justify-space-between enable-counter-bottom-switch">
                  <div class="counter-bottom-settings__text">Enable counter to the bottom</div>
                  <v-switch
                    v-model="liveChatData.counterBottom.enabled"
                    color="primary"
                    :ripple="false"
                    hide-details
                    inset></v-switch>
                </div>
                <v-expand-transition>
                  <div v-if="liveChatData.counterBottom.enabled" class="expandable-content">
                    <div class="d-flex spacing-bottom--16 spacing-top--16 counter-bottom-time-settings">
                      <va-text-input
                        class="flex-1 mr-3"
                        v-model.trim="liveChatData.counterBottom.minutes"
                        label="Minutes"
                        type="number"
                        placeholder="02"></va-text-input>
                      <va-text-input
                        class="flex-1"
                        v-model.trim="liveChatData.counterBottom.seconds"
                        type="number"
                        label="Seconds"
                        placeholder="00"></va-text-input>
                    </div>

                    <div class="spacing-bottom--16">
                      <va-text-input
                        class="flex-1"
                        v-model.trim="liveChatData.counterBottom.title"
                        label="Title on the right side"
                        placeholder="Left to claim"></va-text-input>
                    </div>
                    <div>
                      <va-text-input
                        class="flex-1"
                        v-model.trim="liveChatData.counterBottom.subtitle"
                        label="Sutitle on the right side"
                        placeholder="An available agent is reserved for this time!"></va-text-input>
                    </div>
                  </div>
                </v-expand-transition>
              </div>
              <div class="d-flex justify-center">
                <v-btn :color="'primary'" class="mr-2" v-if="displayAddMessageBtn" @click="addMessage">
                  Add message
                </v-btn>
              </div>
            </div>
          </div>
          <!--Chatbot settings END-->
          <!--Messages -->
          <div class="message-tree">
            <LiveChatTree :message-tree="liveChatData.messages"></LiveChatTree>
          </div>
          <!--Messages END -->
        </div>
      </div>
    </template>
  </div>
</template>

<script>
  // Components
  import LiveChatTree from "@/components/shortcodes/livechatV2/LiveChatTree.vue";
  import LiveChatJsonMode from "@/components/shortcodes/livechatV2/LiveChatJsonMode.vue";
  import Panzoom from "@panzoom/panzoom";
  import VaColorPicker from "@/components/custom/VaColorPicker";
  // Helpers
  import cloneDeep from "lodash/cloneDeep";
  import { v4 as uuidv4 } from "uuid";
  import { convertAssetsPathToAbsoluteLinks } from "@/utility/convertAssetsPaths";
  import { traverseTree } from "@/utility/traverseTree";
  import { syncTelHrefErrors } from "@/utility/validateLiveChatTelHrefs";
  import { flowErrorMessage, normalizeIds } from "@/components/shortcodes/livechatV2/treeGraph";
  // Constants
  import { MESSAGE, LIVE_CHAT_THEME } from "@/components/shortcodes/livechat/constant";
  import definedAbility from "@/ability";

  const DEFAULT_COUNTER_BOTTOM = {
    enabled: false,
    minutes: "",
    seconds: "",
    title: "",
    subtitle: "",
  };

  export default {
    name: "LiveChatDialogV2",
    components: { LiveChatTree, LiveChatJsonMode, VaColorPicker },
    data() {
      return {
        LIVE_CHAT_THEME,
        ability: definedAbility(),
        panzoomInstance: null,
        panzoomWheelParent: null,
        liveChatData: {
          botName: "Mary",
          botAvatar: {
            imageID: "",
            imageURL: "",
          },
          botMessageBackgroundColor: "#f0f0f0",
          actionItemBackgroundColor: "#007bee",
          userMessageBackgroundColor: "#007bee",
          userMessageTextColor: "#FFFFFF",
          messages: [],
          animatedButton: false,
          theme: "",
          counterBottom: { ...DEFAULT_COUNTER_BOTTOM },
          editorMode: "tree",
        },
        paramData: null,
      };
    },
    computed: {
      displayAddMessageBtn() {
        return this.liveChatData.messages && this.liveChatData.messages.length === 0;
      },
      flowError() {
        return flowErrorMessage(this.liveChatData.messages);
      },
    },
    watch: {
      liveChatData: {
        handler(newValue) {
          this.updateValue(newValue);
        },
        deep: true,
      },
      "liveChatData.editorMode"(mode) {
        if (mode !== "json") {
          this.$nextTick(() => this.initPanzoom());
        }
      },
    },
    beforeMount() {
      this.setupReceiveDataListener();
      this.requestData();
    },
    mounted() {
      this.initPanzoom();
    },
    beforeDestroy() {
      this.teardownPanzoom();
    },
    methods: {
      initPanzoom() {
        if (!this.$refs.panzoom) return;
        // Re-runs on each Tree↔JSON toggle — drop the previous instance/listener first.
        this.teardownPanzoom();

        this.panzoomInstance = Panzoom(this.$refs.panzoom, {
          canvas: true,
          excludeClass: "panzoom-ignore",
          handleStartEvent: () => {},
        });

        this.$refs.panzoom.style.height = `${window.innerHeight - 24}px`;

        this.panzoomWheelParent = this.$refs.panzoom.parentElement;
        this.panzoomWheelParent.addEventListener("wheel", this.onPanzoomWheel);
      },
      teardownPanzoom() {
        if (this.panzoomWheelParent) {
          this.panzoomWheelParent.removeEventListener("wheel", this.onPanzoomWheel);
          this.panzoomWheelParent = null;
        }
        if (this.panzoomInstance) {
          this.panzoomInstance.destroy();
          this.panzoomInstance = null;
        }
      },
      onPanzoomWheel(event) {
        if (!this.panzoomInstance) return;
        if (!event.shiftKey) {
          this.panzoomInstance.pan(-event.deltaX, -event.deltaY, { relative: true });

          return;
        }
        this.panzoomInstance.zoomWithWheel(event);
      },
      addMessage() {
        const message = cloneDeep(MESSAGE);
        message.id = uuidv4();
        message.openEditorOnCreate = true;
        this.liveChatData.messages.push(message);
      },
      reset() {
        if (this.panzoomInstance) this.panzoomInstance.reset();
      },
      zoomIn() {
        if (this.panzoomInstance) this.panzoomInstance.zoomIn();
      },
      zoomOut() {
        if (this.panzoomInstance) this.panzoomInstance.zoomOut();
      },
      updateAvatar(value) {
        if (value) {
          this.liveChatData.botAvatar.imageID = value["@id"];

          if (this.ability.can("upload", "media object to inulti server")) {
            // Keep relative image URL, instead of appending inulti server URL. This way we discard inulti reference from generated pages;
            this.liveChatData.botAvatar.imageURL = value.contentUrl;
          } else {
            this.liveChatData.botAvatar.imageURL = value.contentUrl;
          }
        } else {
          this.liveChatData.botAvatar.imageID = "";
          this.liveChatData.botAvatar.imageURL = "";
        }
      },
      updateValue(val) {
        window.parent.postMessage(
          {
            mceAction: "updateLiveChatData",
            data: val,
          },
          origin,
        );
      },
      syncTelHrefErrors() {
        syncTelHrefErrors(this.liveChatData.messages, this);
      },
      setupReceiveDataListener() {
        window.onmessage = message => {
          if (!message.data || typeof message.data !== "object") return;

          if (message.data.type === "telHrefValidationFailed") {
            this.syncTelHrefErrors();
            return;
          }

          if (message.data.type === "sendData") {
            const { data } = message.data;
            traverseTree(data.messages, message => {
              if (message.content) {
                message.content = convertAssetsPathToAbsoluteLinks(message.content);
              }

              if (message.text) {
                message.text = convertAssetsPathToAbsoluteLinks(message.text);
              }

              if (message.value) {
                message.value = convertAssetsPathToAbsoluteLinks(message.value);
              }
            });
            // Id-less nodes are dropped by the graph (blank canvas).
            normalizeIds(data.messages);

            this.liveChatData = {
              ...data,
              counterBottom: { ...DEFAULT_COUNTER_BOTTOM, ...data.counterBottom },
              editorMode: data.editorMode === "json" ? "json" : "tree",
            };
          }
        };
      },
      requestData() {
        window.parent.postMessage(
          {
            mceAction: "requestData",
          },
          origin,
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/typography/index.scss";
  @import "@core/preset/preset/functions.scss";
  .zoom-actions {
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 100;
  }
  .mode-toggle {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 102;
  }

  .flow-error {
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 101;
    max-width: 520px;
    padding: 8px 12px;
    border: 1px solid get-color("error");
    border-radius: 6px;
    background: get-color("element-bg-100");
    color: get-color("error");
    @include text-body-2-bold();
  }

  .title {
    color: get-color("subtitle");
    @include heading-4();
  }

  .chatbot-data {
    border: 1px dashed get-color("primary");
    border-radius: 5px;
    background: get-color("element-bg-100");
    padding: 5px 10px;
    max-width: 600px;
    min-width: 600px;
  }

  .message-tree {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    .message-item {
      border: 1px dashed get-color("primary");
      border-radius: 5px;
      background: get-color("element-bg-100");
      padding: 5px 10px;
      &__settings-wrapper {
        display: flex;
        align-items: center;
      }
      .message-item__settings {
        &--delay {
          max-width: 100px;
        }
        &--human {
          margin-left: 10px;
        }
        &--no-icon {
          margin-left: 10px;
        }
      }
    }
  }

  .counter-bottom-settings {
    width: 100%;
    padding: 12px;
    background-color: get-color("primary-10-hover");
    border-radius: 6px;
    margin-bottom: 16px;

    &__text {
      @include text-body-2-bold();
      color: get-color("headline");
    }
  }
  .counter-bottom-time-settings {
    max-width: 230px;
  }
  .enable-counter-bottom-switch {
    & > div {
      display: flex;
      align-items: center;
      justify-content: space-between;

      &::v-deep {
        .v-input--selection-controls__input {
          margin-right: 0;
        }
      }
    }
  }
</style>
